import { createFileRoute } from '@tanstack/react-router';
import { Boxes, Bell, Plus, Sparkles } from 'lucide-react';
import { ComponentTabs } from '@/components/glitch-ui/component-tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { useState } from 'react';

export const Route = createFileRoute('/_authenticated/_layout/components/animations')({
  component: AnimationsPage,
  staticData: {
    title: 'Components',
    icon: Boxes,
  },
});

function AnimationsPage() {
  const [isSpringSelected, setIsSpringSelected] = useState(false);

  return (
    <div className="flex flex-col h-full">
      <ComponentTabs />

      <div className="flex-1 pb-8 space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Loading Animation</h3>
              <p className="text-gray-900">CSS keyframe animation for loading states</p>
              <div className="flex items-center justify-center gap-4 p-8 bg-gray-50 rounded-md">
                <div className="w-12 h-12 rounded-full border-4 border-gray-200 border-t-[#290BD9] animate-spin"></div>
              </div>
              <div className="p-4 bg-gray-50 rounded-md">
                <code className="text-sm text-gray-900">
                  animation: loading 1s steps(4, jump-none) infinite
                </code>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Vibrate Animation</h3>
              <p className="text-gray-900">Attention-grabbing shake effect</p>
              <div className="flex items-center justify-center p-8 bg-gray-50 rounded-md">
                <div className="animate-vibrate">
                  <Bell className="w-8 h-8 text-green-500" />
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-md">
                <code className="text-sm text-gray-900">
                  animation: vibrate 4s ease-in-out infinite
                </code>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Wiggle Animation</h3>
              <p className="text-gray-900">Custom wiggle animation for campaign dashboard</p>
              <div className="flex items-center justify-center p-8 bg-gray-50 rounded-md">
                <style>{`
                  @keyframes wiggle {
                    0%, 100% { transform: translateX(0); }
                    10%, 50%, 90% { transform: translateX(-8px); }
                    30%, 70% { transform: translateX(8px); }
                  }
                  .animate-wiggle {
                    animation: wiggle 2s ease-in-out infinite;
                  }
                `}</style>
                <div className="animate-wiggle">
                  <Button className="bg-[#290BD9] hover:bg-[#2309C5] text-white">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                  </Button>
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-md">
                <code className="text-sm text-gray-900">
                  Custom keyframe animation with horizontal translation
                </code>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Shimmer Animation</h3>
              <p className="text-gray-900">Shimmer effect for interactive hover states</p>
              <div className="flex items-center justify-center p-8 bg-gray-50 rounded-md overflow-hidden">
                <div className="h-20 w-full bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] animate-shimmer rounded-md"></div>
              </div>
              <div className="p-4 bg-gray-50 rounded-md">
                <code className="text-sm text-gray-900">
                  animation: shimmer 1.5s ease-out infinite
                </code>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Spring Animation</h3>
              <p className="text-gray-900">Framer Motion spring for selection states</p>
              <div className="flex items-center justify-center p-8 bg-gray-50 rounded-md">
                <motion.div
                  animate={isSpringSelected ? { scale: 1.05 } : { scale: 1 }}
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 10,
                    duration: 0.3
                  }}
                  onClick={() => setIsSpringSelected(!isSpringSelected)}
                  className="cursor-pointer"
                >
                  <div className="bg-[#290BD9] text-white px-6 py-3 rounded-lg flex items-center space-x-2">
                    <Sparkles className="h-5 w-5" />
                    <span>Click me</span>
                  </div>
                </motion.div>
              </div>
              <div className="p-4 bg-gray-50 rounded-md">
                <code className="text-sm text-gray-900">
                  stiffness: 400, damping: 10
                </code>
              </div>
            </CardContent>
          </Card>
        </div>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Transition Effects</h2>
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6 space-y-4">
              <p className="text-gray-900 mb-4">Common transition effects</p>
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors duration-200">
                  <p className="text-gray-900">Hover me - Color transition (200ms)</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-md hover:scale-105 transition-transform duration-200">
                  <p className="text-gray-900">Hover me - Scale transition (200ms)</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-md hover:shadow-lg transition-shadow duration-200">
                  <p className="text-gray-900">Hover me - Shadow transition (200ms)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
